import { useState } from "react";
import { useTranslation } from "react-i18next";
import { cx } from "./ui";

export interface UsagePart {
  /** Named in the readout, e.g. "apps". */
  label: string;
  value: number;
}

export interface UsagePoint {
  day: string;
  value: number;
  /**
   * What the day was made of, named in the readout rather than drawn.
   *
   * Deliberately not a stacked bar. A stack needs a categorical colour scale and
   * this design system does not have one: there is a single accent, and `warn`
   * and `danger` are documented as state rather than identity — so three kinds
   * would mean either three tints of one blue, indistinguishable at a bar four
   * pixels wide, or inventing an identity palette for one chart. The composition
   * is exact in the breakdown above the chart and exact again in this readout on
   * hover, which is where a reader asking "what was Tuesday" actually looks.
   *
   * Optional, and absent for the AI page's token and request series, which have
   * no composition to name.
   */
  parts?: UsagePart[];
}

/**
 * A 30-day bar chart, over whatever the caller is measuring.
 *
 * This started as `SpendChart` and knew about money. The AI page needed the
 * same chart over token counts, and copying it would have produced two charts
 * that agree today and drift the first time either is touched — the same
 * failure that once left four hand-rolled badges in the admin console with four
 * different geometries. `SpendChart` survived for a while as a thin wrapper and
 * has been removed: the billing page needs to pass a day's composition through,
 * which a wrapper taking `SpendDay[]` and a currency could not carry. Every
 * caller now uses this directly, so there is one bar chart and one place for it
 * to be wrong.
 *
 * Bars are proportional to the window's maximum, and a zero day stays a
 * hairline so the whole window reads as a span rather than a scattering of
 * islands.
 *
 * A series that is all zeros is drawn, not replaced with the empty message. It
 * used to be replaced, and that was a lie the moment free models existed: a
 * month of traffic on a model priced at zero produced a spend chart reading "No
 * requests in this window" under a strip counting four thousand of them. Nothing
 * to plot and nothing spent are different facts, and only the first one is
 * absence — so the empty message is now reserved for a window with no days in
 * it, and a flat window renders flat.
 */
export function UsageChart({
  points,
  format,
  label: seriesLabel,
  emptyLabel,
}: {
  points: UsagePoint[];
  /** Turns a value into what the tooltip and readout show. */
  format: (value: number) => string;
  /** Describes the series for a screen reader, e.g. "Daily spend". */
  label: string;
  emptyLabel?: string;
}) {
  const { t } = useTranslation("common");
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const max = points.reduce((m, point) => Math.max(m, point.value), 0);

  if (points.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-fg-subtle">
        {emptyLabel ?? t("chart.empty")}
      </p>
    );
  }

  /*
   * Every day zero. The bars sit at the hairline minimum and lose their fill,
   * because a full-strength bar of the same height as its neighbours reads as a
   * measurement rather than as the floor — and dividing by this max would put
   * `NaN%` in the style attribute.
   */
  const flat = max === 0;
  const selected = points.find((point) => point.day === selectedDay) ?? points.at(-1);

  return (
    <div>
      <div
        className="flex h-24 items-end gap-[2px]"
        aria-label={t("chart.last30Days", { series: seriesLabel })}
      >
        {points.map((point) => {
          const readout = describe(point, format);
          return (
            <button
              key={point.day}
              type="button"
              title={readout}
              aria-label={readout}
              aria-pressed={selected?.day === point.day}
              onClick={() => setSelectedDay(point.day)}
              onFocus={() => setSelectedDay(point.day)}
              className="group relative flex h-full flex-1 items-end focus:outline-none"
            >
              <span
                className={cx(
                  "w-full rounded-t-[2px] transition-colors group-hover:bg-accent",
                  flat
                    ? selected?.day === point.day
                      ? "bg-accent/60"
                      : "bg-line-strong"
                    : selected?.day === point.day
                      ? "bg-accent"
                      : "bg-accent/70",
                )}
                style={{ height: flat ? "2%" : `${Math.max(2, (point.value / max) * 100)}%` }}
              />
            </button>
          );
        })}
      </div>
      <div className="mt-1.5 flex justify-between text-[0.6875rem] text-fg-subtle">
        <span>{dayLabel(points[0]?.day)}</span>
        <span>{dayLabel(points[points.length - 1]?.day)}</span>
      </div>
      {selected && (
        <p className="mt-3 text-xs text-fg-muted" aria-live="polite">
          {describe(selected, format)}
        </p>
      )}
    </div>
  );
}

/**
 * One day, as a line of text: the total, then whatever it was made of.
 *
 * Shared by the tooltip, the accessible label and the readout, so a screen reader
 * and a pointer get the same sentence. Zero parts are dropped — "apps $0.00" in a
 * breakdown is a row saying nothing, and on a stacked chart it is a segment that
 * is not there.
 */
function describe(point: UsagePoint, format: (value: number) => string): string {
  const parts = (point.parts ?? []).filter((part) => part.value > 0);
  const total = `${dayLabel(point.day)}: ${format(point.value)}`;
  if (parts.length < 2) return total;

  return `${total} — ${parts.map((part) => `${part.label} ${format(part.value)}`).join(", ")}`;
}

function dayLabel(iso: string | undefined): string {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
